import React, { useRef, useState, useCallback, useEffect } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { ClipboardList, Film, Clapperboard, CheckCircle } from 'lucide-react';
import { DemoStage } from './DemoChrome';
import { useDemoAutoplay } from './useDemoAutoplay';
import { AngelWingsCard } from './AngelWingsCard';
import { WorkflowMorphCard } from './WorkflowMorphCard';
import { DEMO_LIBRARY_VIDEO_THUMB } from './demoStockImages';
import { pixelsToRem } from '../../lib/layoutUnits';
import './marketingDemos.css';

const WORKFLOW_STEPS = [
  {
    id: 'planning',
    label: 'Planning',
    status: 'Brief approved',
    Icon: ClipboardList,
    color: '#6366f1',
    item: 'April content brief',
    desc: 'Strategy doc approved before anyone shoots or edits',
  },
  {
    id: 'media',
    label: 'Library media',
    status: 'In review',
    Icon: Film,
    color: '#0f766e',
    item: 'Brand interview',
    desc: 'Raw footage reviewed with timestamped notes',
  },
  {
    id: 'draft',
    label: 'Draft',
    status: 'Ready to publish',
    Icon: Clapperboard,
    color: '#3b82f6',
    item: 'Instagram reel cut',
    desc: 'Platform-specific draft from the approved clip',
  },
  {
    id: 'published',
    label: 'Published',
    status: 'Live',
    Icon: CheckCircle,
    color: '#059669',
    item: 'Live on Instagram',
    desc: 'Only the approved version ships to the calendar and goes live',
  },
];

export default function EndToEndWorkflowDemo() {
  const reduceMotion = useReducedMotion();
  const rootRef = useRef(null);
  const trackRef = useRef(null);
  const cardWrapRef = useRef(null);
  const [loopKey, setLoopKey] = useState(0);
  const [stepIndex, setStepIndex] = useState(0);
  const [cardStageId, setCardStageId] = useState('planning');
  const [flyingAway, setFlyingAway] = useState(false);
  const [flyOrigin, setFlyOrigin] = useState(null);

  const resetLoop = useCallback(() => {
    setFlyingAway(false);
    setFlyOrigin(null);
    setStepIndex(0);
    setCardStageId('planning');
    setLoopKey((key) => key + 1);
  }, []);

  const triggerFlyAway = useCallback(() => {
    const card = cardWrapRef.current;
    const container = trackRef.current;
    if (card && container) {
      const rect = card.getBoundingClientRect();
      const bounds = container.getBoundingClientRect();
      setFlyOrigin({
        x: pixelsToRem(rect.left - bounds.left + rect.width / 2),
        y: pixelsToRem(rect.top - bounds.top + rect.height / 2),
      });
    }
    setFlyingAway(true);
  }, []);

  const onAutoplayTick = useCallback(() => {
    if (flyingAway) return;

    setStepIndex((currentIndex) => {
      if (currentIndex === WORKFLOW_STEPS.length - 1) {
        triggerFlyAway();
        return currentIndex;
      }

      const nextIndex = currentIndex + 1;

      if (nextIndex === WORKFLOW_STEPS.length - 1) {
        setCardStageId('published');
      } else if (nextIndex === 1) {
        setCardStageId('media');
      } else if (nextIndex === 2) {
        setCardStageId('draft');
      }

      return nextIndex;
    });
  }, [flyingAway, triggerFlyAway]);

  useDemoAutoplay(rootRef, onAutoplayTick, flyingAway ? 1600 : 1150);

  useEffect(() => {
    const preload = new Image();
    preload.src = DEMO_LIBRARY_VIDEO_THUMB;
  }, []);

  useEffect(() => {
    if (!flyingAway) return undefined;
    const holdMs = reduceMotion ? 900 : 1400;
    const timer = window.setTimeout(resetLoop, holdMs);
    return () => window.clearTimeout(timer);
  }, [flyingAway, reduceMotion, resetLoop]);

  const planning = WORKFLOW_STEPS[0];
  const media = WORKFLOW_STEPS[1];
  const draft = WORKFLOW_STEPS[2];
  const published = WORKFLOW_STEPS[3];
  const activeStep = WORKFLOW_STEPS[stepIndex];
  const slideColumn = flyingAway ? WORKFLOW_STEPS.length : stepIndex + 1;

  return (
    <div ref={rootRef}>
      <DemoStage
        className="mkt-demo-stage--fly-visible"
        visualClassName="mkt-demo-visual--workflow"
      >
        <div className="mkt-workflow-slide-demo">
          <div className="mkt-pipeline-stepper mkt-pipeline-stepper--workflow">
            {WORKFLOW_STEPS.map((step, index) => {
              const Icon = step.Icon;
              const isPast = index < stepIndex;
              const isPublishedStep = index === WORKFLOW_STEPS.length - 1;
              const isCurrent =
                (index === stepIndex && !flyingAway) || (flyingAway && isPublishedStep);

              return (
                <div key={step.id} className="mkt-pipeline-stepper-segment">
                  <motion.div
                    className={`mkt-pipeline-step-node mkt-pipeline-step-node--auto${isCurrent ? ' mkt-pipeline-step-node--current' : ''}${isPast || (flyingAway && isPublishedStep) ? ' mkt-pipeline-step-node--done' : ''}${flyingAway && isPublishedStep ? ' mkt-pipeline-step-node--launch' : ''}`}
                    animate={
                      reduceMotion || !isCurrent
                        ? undefined
                        : {
                            boxShadow: [
                              '0 0 0 0 rgba(59,130,246,0.35)',
                              '0 0 0 0.4rem rgba(59,130,246,0)',
                              '0 0 0 0 rgba(59,130,246,0.35)',
                            ],
                          }
                    }
                    transition={{ duration: 1.4, repeat: Infinity }}
                  >
                    <Icon className="ui-icon-lg" aria-hidden />
                  </motion.div>
                  <span
                    className={`mkt-pipeline-step-label mkt-pipeline-step-label--workflow${isCurrent ? ' mkt-pipeline-step-label--current' : ''}`}
                  >
                    <strong>{step.label}</strong>
                  </span>
                  {index < WORKFLOW_STEPS.length - 1 ? (
                    <span
                      className={`mkt-pipeline-step-connector${isPast ? ' mkt-pipeline-step-connector--filled' : ''}`}
                    />
                  ) : null}
                </div>
              );
            })}
          </div>

          <div ref={trackRef} className="mkt-workflow-slide-track" aria-hidden>
            <motion.div
              key={loopKey}
              ref={cardWrapRef}
              className={`mkt-workflow-slide-card-wrap${flyingAway ? ' mkt-workflow-slide-card-wrap--ghost' : ''}`}
              style={{ gridColumn: slideColumn }}
              layout={!reduceMotion}
              transition={
                reduceMotion
                  ? { duration: 0 }
                  : { type: 'spring', stiffness: 140, damping: 24, mass: 0.8 }
              }
            >
              <div className="mkt-workflow-slide-card">
                <span
                  className="mkt-workflow-slide-status"
                  style={{ '--workflow-accent': activeStep.color }}
                >
                  {activeStep.status}
                </span>
                <WorkflowMorphCard
                  stageId={cardStageId}
                  planning={planning}
                  media={media}
                  draft={draft}
                  published={published}
                />
              </div>
            </motion.div>

            {flyingAway && flyOrigin ? (
              <div
                className={`mkt-workflow-flying-anchor mkt-workflow-flying-anchor--swoosh${reduceMotion ? ' mkt-workflow-flying-anchor--reduce-motion' : ''}`}
                style={{
                  left: `${flyOrigin.x}rem`,
                  top: `${flyOrigin.y}rem`,
                }}
              >
                <AngelWingsCard
                  ascending
                  flyMotion={false}
                  quietWings
                  className="mkt-workflow-flying-card"
                >
                  <WorkflowMorphCard
                    stageId="published"
                    planning={planning}
                    media={media}
                    draft={draft}
                    published={published}
                  />
                </AngelWingsCard>
              </div>
            ) : null}
          </div>
        </div>
      </DemoStage>
    </div>
  );
}
